import { useState } from 'react'
import { useAuth } from '../../../context/AuthContext'
import { AdminAlert } from '../AdminShared'
import { Skeleton } from '../components/Skeleton'
import { adminGetUsers, adminGetUserById, adminAddCPT, adminReduceCPT, adminAddCisScore, adminSubtractCisScore } from '../../../services/admin'

export function CptCisAllocation() {
  const { canDistributeCpt, hasPermission } = useAuth()
  const canCis = hasPermission('cis')
  const canAdjust = (type) => (type === 'cpt' ? canDistributeCpt : canCis)
  const [search, setSearch] = useState('')
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [searched, setSearched] = useState(false)
  const [error, setError] = useState(null)

  const [adjustModal, setAdjustModal] = useState(null) // { user, type: 'cpt'|'cis', action: 'add'|'reduce'|'subtract' }
  const [adjustAmount, setAdjustAmount] = useState('')
  const [adjustReason, setAdjustReason] = useState('')
  const [adjustLoading, setAdjustLoading] = useState(false)
  const [adjustError, setAdjustError] = useState(null)
  const [successMsg, setSuccessMsg] = useState(null)

  const runSearch = async (q = search) => {
    if (!q.trim()) { setResults([]); setSearched(false); return }
    setLoading(true)
    setError(null)
    setSearched(true)
    try {
      const res = await adminGetUsers({ search: q, filter: 'all', page: 1, limit: 10 })
      setResults(Array.isArray(res?.data) ? res.data : [])
    } catch (err) {
      setError(err)
      setResults([])
    } finally {
      setLoading(false)
    }
  }

  const openAdjust = (user, type, action) => {
    setAdjustModal({ user, type, action })
    setAdjustAmount('')
    setAdjustReason('')
    setAdjustError(null)
  }
  const closeAdjust = () => { setAdjustModal(null); setAdjustError(null) }

  const handleAdjustSubmit = async () => {
    const amt = Number(adjustAmount)
    if (!amt || amt <= 0) { setAdjustError('Enter a valid positive number'); return }
    if (adjustModal.type === 'cpt' && !adjustReason.trim()) { setAdjustError('Reason is required for CPT adjustments'); return }
    setAdjustLoading(true)
    setAdjustError(null)
    try {
      const { user, type, action } = adjustModal
      const full = await adminGetUserById(user.id)
      const profileId = full?.profile?.id ?? full?.data?.profile?.id
      if (!profileId) {
        setAdjustError('Could not resolve this user\'s profile ID — they may not have a profile yet.')
        setAdjustLoading(false)
        return
      }

      if (type === 'cpt') {
        if (action === 'add') await adminAddCPT(profileId, amt, adjustReason)
        else await adminReduceCPT(profileId, amt, adjustReason)
      } else {
        if (action === 'add') await adminAddCisScore(profileId, amt)
        else await adminSubtractCisScore(profileId, amt)
      }
      closeAdjust()
      setSuccessMsg(`${action === 'add' ? 'Added' : action === 'reduce' ? 'Reduced' : 'Subtracted'} ${amt} ${type.toUpperCase()} for ${user.displayName || user.username || user.email}`)
      setTimeout(() => setSuccessMsg(null), 4000)
      runSearch()
    } catch (err) {
      setAdjustError(err.message ?? 'Adjustment failed')
    } finally {
      setAdjustLoading(false)
    }
  }

  return (
    <>
      {adjustModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-on-background/60 p-4" onClick={e => { if (e.target === e.currentTarget) closeAdjust() }}>
          <div className="bg-surface border-4 border-on-background w-full max-w-sm flex flex-col gap-5 p-6"
            style={{ boxShadow: '6px 6px 0px 0px var(--neo-border-color)' }}>
            <div className="flex items-center justify-between">
              <h2 className="font-extrabold text-[14px] uppercase tracking-widest">
                {adjustModal.action === 'add' ? '+ Add' : '− Reduce'} {adjustModal.type.toUpperCase()}
              </h2>
              <button onClick={closeAdjust} className="p-1 hover:bg-surface-container transition-colors">
                <span className="material-symbols-outlined text-[20px]">close</span>
              </button>
            </div>
            <p className="text-[12px] text-on-surface-variant">
              User: <span className="font-bold text-on-surface">{adjustModal.user.displayName || adjustModal.user.username || adjustModal.user.email}</span>
            </p>
            {adjustError && <AdminAlert type="error" message={adjustError} />}
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <label className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">Amount</label>
                <input
                  type="number" min="1" placeholder="e.g. 100"
                  value={adjustAmount} onChange={e => setAdjustAmount(e.target.value)}
                  className="bg-surface border-2 border-on-background px-3 py-2 font-mono text-[14px] focus:outline-none focus:border-primary-container"
                />
              </div>
              {adjustModal.type === 'cpt' && (
                <div className="flex flex-col gap-1">
                  <label className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">Reason <span className="text-error">*</span></label>
                  <input
                    type="text" placeholder="e.g. Manual adjustment for testing"
                    value={adjustReason} onChange={e => setAdjustReason(e.target.value)}
                    className="bg-surface border-2 border-on-background px-3 py-2 text-[13px] focus:outline-none focus:border-primary-container"
                  />
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <button onClick={closeAdjust}
                className="flex-1 py-2.5 border-2 border-on-background font-bold text-[11px] uppercase tracking-widest hover:bg-surface-container transition-colors">
                Cancel
              </button>
              <button onClick={handleAdjustSubmit} disabled={adjustLoading}
                className="flex-1 py-2.5 bg-on-background text-surface font-bold text-[11px] uppercase tracking-widest hover:opacity-90 transition-opacity disabled:opacity-50">
                {adjustLoading ? 'Saving…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-6">
        {successMsg && <AdminAlert type="success" message={successMsg} />}
        {error && <AdminAlert type="error" message={error.message} />}

        <div className="border-4 border-on-background bg-surface-container-low p-5 flex flex-col gap-1"
          style={{ boxShadow: '5px 5px 0px 0px var(--neo-border-color)' }}>
          <p className="font-bold text-[11px] uppercase tracking-widest text-on-surface-variant">How this works</p>
          <p className="text-[12px] text-on-surface-variant">
            Search for a user by email, username, or display name, then add or reduce their CPT (points) or CIS (score) directly.
          </p>
          {!canDistributeCpt && (
            <p className="text-[12px] font-bold text-error mt-1">
              You don't have permission to distribute CPT. Ask the super admin to enable it for your account.
            </p>
          )}
          {!canCis && (
            <p className="text-[12px] font-bold text-error mt-1">
              You don't have the CIS permission. Ask the super admin to grant it to adjust CIS scores.
            </p>
          )}
        </div>

        <div className="relative w-full flex gap-2">
          <div className="relative flex-1">
            <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-[18px] text-on-surface-variant">search</span>
            <input
              type="text" placeholder="Search by email, username, or name…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') runSearch() }}
              className="w-full bg-surface border-4 border-on-background pl-11 pr-4 py-3 font-body-md focus:ring-0 focus:outline-none focus:border-primary-container transition-all"
            />
          </div>
          <button onClick={() => runSearch()}
            className="px-6 border-4 border-on-background bg-on-background text-surface font-bold text-[11px] uppercase tracking-widest hover:opacity-90 transition-opacity"
            style={{ boxShadow: '3px 3px 0px 0px var(--neo-border-color)' }}>
            Search
          </button>
        </div>

        {loading ? (
          <div className="flex flex-col gap-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-20 w-full rounded" />
            ))}
          </div>
        ) : !searched ? (
          <div className="border-4 border-dashed border-on-background/25 bg-surface-container-low p-10 text-center">
            <p className="text-on-surface-variant font-bold text-[12px] uppercase tracking-widest">Search for a user to begin</p>
          </div>
        ) : results.length === 0 ? (
          <div className="border-4 border-dashed border-on-background/25 bg-surface-container-low p-10 text-center">
            <p className="text-on-surface-variant font-bold text-[12px] uppercase tracking-widest">No users found</p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {results.map(u => (
              <div key={u.id} className="border-4 border-on-background bg-surface rounded-lg p-4 flex flex-col gap-4"
                style={{ boxShadow: '5px 5px 0px 0px var(--neo-border-color)' }}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex flex-col gap-0.5 min-w-0">
                    <h3 className="font-bold text-[15px] leading-tight break-words">{u.displayName || '—'}</h3>
                    <p className="text-[12px] text-on-surface-variant font-mono break-all">{u.email}</p>
                    <p className="text-[12px] text-on-surface-variant">{u.username ? `@${u.username}` : '—'}</p>
                  </div>
                  <div className="flex gap-4 text-[12px]">
                    <div className="text-right">
                      <p className="text-on-surface-variant font-bold uppercase tracking-wide text-[10px]">CIS</p>
                      <p className="font-mono font-bold">{u.cisPoints ?? 0}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-on-surface-variant font-bold uppercase tracking-wide text-[10px]">CPT</p>
                      <p className="font-mono font-bold">{u.cptEarned ?? 0}</p>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[
                    { label: '+ CPT', type: 'cpt', action: 'add' },
                    { label: '− CPT', type: 'cpt', action: 'reduce' },
                    { label: '+ CIS', type: 'cis', action: 'add' },
                    { label: '− CIS', type: 'cis', action: 'subtract' },
                  ].map(({ label, type, action }) => (
                    <button key={label} onClick={() => openAdjust(u, type, action)}
                      disabled={!canAdjust(type)}
                      title={canAdjust(type) ? undefined : (type === 'cpt' ? 'Ask the super admin to grant CPT distribution' : 'Ask the super admin to grant the CIS permission')}
                      className="disabled:opacity-40 disabled:cursor-not-allowed py-2 border-2 border-on-background font-bold text-[10px] uppercase tracking-widest hover:bg-primary-container hover:text-on-primary-container transition-colors rounded">
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}